/**
 * Safely parse JSON string with error handling
 * @param {string} jsonString - JSON string to parse
 * @returns {Object|null} - Parsed object or null if invalid
 */
export const parseJsonSafely = (jsonString) => {
  if (!jsonString || typeof jsonString !== "string") {
    return null;
  }
  try {
    return JSON.parse(jsonString);
  } catch (err) {
    return null;
  }
};

/**
 * Get the type of a value
 * @param {*} value - Value to check type
 * @returns {string} - Type string (string, number, boolean, object, array, null)
 */
const getValueType = (value) => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
};

/**
 * Generate schema from JSON object recursively
 * @param {Object|Array} jsonObject - JSON object or array to generate schema from
 * @returns {Object} - Schema object with keys, types, and nested schemas
 */
export const generateSchema = (jsonObject) => {
  if (jsonObject === null || jsonObject === undefined) {
    return { keys: [], types: {}, schemas: {} };
  }

  // Handle arrays - generate schema for first element if it's an object
  if (Array.isArray(jsonObject)) {
    if (jsonObject.length === 0) {
      return { keys: [], types: {}, schemas: {}, isArray: true, itemSchema: null };
    }
    const firstItem = jsonObject[0];
    if (
      typeof firstItem === "object" &&
      firstItem !== null &&
      !Array.isArray(firstItem)
    ) {
      return {
        keys: [],
        types: {},
        schemas: {},
        isArray: true,
        itemSchema: generateSchema(firstItem),
      };
    }
    return {
      keys: [],
      types: {},
      schemas: {},
      isArray: true,
      itemSchema: null,
      itemType: getValueType(firstItem),
    };
  }

  // Handle plain objects
  if (typeof jsonObject !== "object") {
    return { keys: [], types: {}, schemas: {} };
  }

  const keys = Object.keys(jsonObject);
  const types = {};
  const schemas = {};

  keys.forEach((key) => {
    const value = jsonObject[key];
    const valueType = getValueType(value);
    types[key] = valueType;

    // Recursively generate schema for nested objects and arrays
    if (valueType === "object" && value !== null && !Array.isArray(value)) {
      schemas[key] = generateSchema(value);
    } else if (valueType === "array" && Array.isArray(value)) {
      schemas[key] = generateSchema(value);
    }
  });

  return {
    keys,
    types,
    schemas,
  };
};

/**
 * Validate JSON object against schema recursively
 * Ensures all keys from schema exist in JSON (prevents removal)
 * Allows new keys to be added
 * @param {Object|Array} jsonObject - JSON object or array to validate
 * @param {Object} schema - Schema object with keys, types, and nested schemas
 * @param {string} path - Current path for error messages (used recursively)
 * @returns {{isValid: boolean, errors: string[], fieldErrors: Record<string, string>}} - Validation result
 */
export const validateAgainstSchema = (jsonObject, schema, path = "") => {
  const errors = [];
  const fieldErrors = {};

  const mergeFieldErrors = (fe) => {
    if (!fe) return;
    Object.keys(fe).forEach((k) => {
      if (fieldErrors[k]) {
        fieldErrors[k] = `${fieldErrors[k]}; ${fe[k]}`;
      } else {
        fieldErrors[k] = fe[k];
      }
    });
  };

  const addFieldError = (fieldPath, message) => {
    if (!fieldPath) return;
    if (fieldErrors[fieldPath]) {
      fieldErrors[fieldPath] = `${fieldErrors[fieldPath]}; ${message}`;
    } else {
      fieldErrors[fieldPath] = message;
    }
  };

  if (!schema) {
    return { isValid: true, errors: [], fieldErrors: {} };
  }

  // Handle array validation
  if (schema.isArray === true) {
    if (!Array.isArray(jsonObject)) {
      const msg = `${path ? `At "${path}": ` : ""}Expected an array`;
      errors.push(msg);
      addFieldError(path, "Expected an array");
      return { isValid: false, errors, fieldErrors };
    }

    if (schema.itemSchema) {
      jsonObject.forEach((item, index) => {
        const itemPath = path ? `${path}[${index}]` : `[${index}]`;
        if (typeof item === "object" && item !== null && !Array.isArray(item)) {
          const itemValidation = validateAgainstSchema(
            item,
            schema.itemSchema,
            itemPath
          );
          errors.push(...itemValidation.errors);
          mergeFieldErrors(itemValidation.fieldErrors);
        } else {
          const msg = `${
            path ? `At "${path}[${index}]": ` : `At index ${index}: `
          }Expected an object but got ${getValueType(item)}`;
          errors.push(msg);
          const shortMsg = `Expected an object but got ${getValueType(item)}`;
          addFieldError(itemPath, shortMsg);
        }
      });
    } else if (schema.itemType) {
      jsonObject.forEach((item, index) => {
        const itemType = getValueType(item);
        if (itemType !== schema.itemType) {
          const msg = `${
            path ? `At "${path}[${index}]": ` : `At index ${index}: `
          }Expected type "${schema.itemType}" but got "${itemType}"`;
          errors.push(msg);
          const itemPath = path ? `${path}[${index}]` : `[${index}]`;
          const shortMsg = `Expected type "${schema.itemType}" but got "${itemType}"`;
          addFieldError(itemPath, shortMsg);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
    };
  }

  // Handle object validation
  if (!jsonObject || typeof jsonObject !== "object" || Array.isArray(jsonObject)) {
    const msg = `${path ? `At "${path}": ` : ""}Value must be a valid JSON object`;
    errors.push(msg);
    addFieldError(path, "Value must be a valid JSON object");
    return { isValid: false, errors, fieldErrors };
  }

  if (!schema.keys || !Array.isArray(schema.keys)) {
    return { isValid: true, errors: [], fieldErrors: {} };
  }

  schema.keys.forEach((key) => {
    const currentPath = path ? `${path}.${key}` : key;
    if (!(key in jsonObject)) {
      const msg = `Key "${currentPath}" is required and cannot be removed`;
      errors.push(msg);
      addFieldError(currentPath, msg);
    } else {
      const value = jsonObject[key];
      const expectedType = schema.types[key];
      const actualType = getValueType(value);

      if (expectedType && actualType !== expectedType) {
        if (!(expectedType === "object" && actualType === "null")) {
          const msg = `Key "${currentPath}" has type "${actualType}" but expected "${expectedType}"`;
          errors.push(msg);
          addFieldError(currentPath, msg);
        }
      }

      if (schema.schemas && schema.schemas[key]) {
        const nestedSchema = schema.schemas[key];
        if (nestedSchema.isArray === true) {
          const arrayValidation = validateAgainstSchema(
            value,
            nestedSchema,
            currentPath
          );
          errors.push(...arrayValidation.errors);
          mergeFieldErrors(arrayValidation.fieldErrors);
        } else if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          const nestedValidation = validateAgainstSchema(
            value,
            nestedSchema,
            currentPath
          );
          errors.push(...nestedValidation.errors);
          mergeFieldErrors(nestedValidation.fieldErrors);
        }
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    fieldErrors,
  };
};

/**
 * Extract data and schema from stored value
 * @param {string} valueString - Stringified JSON value
 * @returns {{data: Object, schema: Object}} - Extracted data and schema
 */
export const extractDataAndSchema = (valueString) => {
  const parsed = parseJsonSafely(valueString);
  if (!parsed) {
    return { data: {}, schema: { keys: [], types: {} } };
  }

  // Check if it has the structure { data: {...}, schema: {...} }
  if (parsed.data && parsed.schema) {
    return {
      data: parsed.data,
      schema: parsed.schema,
    };
  }

  // If it's a plain object, generate schema from it
  return {
    data: parsed,
    schema: generateSchema(parsed),
  };
};

/**
 * Merge data with schema for storage
 * @param {Object} data - User's JSON data
 * @param {Object} schema - Schema object
 * @returns {Object} - Merged object ready for storage
 */
export const mergeDataWithSchema = (data, schema) => {
  return {
    data,
    schema,
  };
};
